import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sharedStyles } from '@lib/sharedStyles';
import { CustomEventType } from '@/events/CustomEventRegistry';

@customElement('jeopardy-grid-display')
export default class JeopardyGridDisplay extends LitElement {
  static styles = [
    sharedStyles,
    css`
      :host {
        display: block;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        padding: 1vmin;
      }

      .grid-wrap {
        width: 100%;
        height: 100%;
        display: grid;
        gap: 1vmin;
      }

      .category-cell {
        container-type: size;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        font-family: var(--font-headline);
        color: black;
        background: var(--color-pink);
        border: var(--border-default);
        border-radius: var(--border-radius);
        box-shadow: var(--shadow-default);
      }

      .category-label {
        font-size: max(1rem, 50cqh);
      }

      .cell-btn {
        container-type: size;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;

        /* Button Styles from BasicButton */
        font-family: var(--font-headline);
        color: black;
        border: var(--border-default);
        border-radius: var(--border-radius);
        cursor: pointer;
        box-shadow: var(--shadow-default);
        transition: all 0.3s ease;

        /* Reset button defaults */
        appearance: none;
        text-transform: uppercase;
        overflow: hidden; /* Ensure no spill */
      }

      .cell-value {
        font-size: max(1rem, 20cqh);
      }

      .cell-btn:hover:not(:disabled) {
        filter: brightness(1.1);
        box-shadow: 0 8px 25px rgba(0,0,0,0.2);
        transform: translateY(-2px);
      }

      .cell-btn:disabled {
        opacity: 0.2;
        cursor: default;
        box-shadow: none;
        border-color: rgba(0,0,0,0.2);
      }

      .overlay { position: fixed; inset: 0; display:flex; align-items:center; justify-content:center; background: rgba(0,0,0,0.45); z-index: 9999; }
      .popup { 
        background: #b1dcf1; 
        padding: 6vmin; 
        border: var(--border-default); 
        border-radius: var(--border-radius); 
        max-width: 95vw; 
        width: auto; 
        min-width: 70vw;
        box-shadow: var(--shadow-default); 
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        gap: 4vmin;
      }
      .popup-meta { 
        font-family: var(--font-headline);
        font-size: clamp(2rem, 4vmin, 4rem);
        color: black;
        opacity: 0.6;
        text-transform: uppercase;
      }
      .popup-question { 
        font-family: var(--font-headline);
        font-size: clamp(3rem, 7vmin, 8rem);
        color: black;
        line-height: 1.1;
      }
      .popup-answer { 
        font-family: var(--font-headline);
        font-size: clamp(2rem, 5vmin, 6rem);
        color: black;
        margin-top: 3vmin;
        padding-top: 3vmin;
        border-top: 3px solid rgba(0,0,0,0.15);
        width: 100%;
      }
    `
  ];

  @property({ type: Object }) quiz: any | null = null;
  @property({ type: Object }) popupQuestion: any | null = null;
  @property({ type: Boolean, reflect: true }) showAnswer: boolean = false;
  @property({ type: Object }) usedCells: Set<string> | null = null;

  @state() private _showPopup: boolean = false;
  @state() private _usedCells: Set<string> = new Set();

  updated(changed: Map<string, any>) {
    if (changed.has('popupQuestion')) {
      this._showPopup = !!this.popupQuestion;
      const prev = changed.get('popupQuestion');
      if (!this.popupQuestion && prev) {
        this._markQuestionAsUsed(prev);
      }
    }
    // Ensure popup is shown if question is present (robustness fix)
    if (this.popupQuestion && !this._showPopup) {
      this._showPopup = true;
    }
  }

  private _markQuestionAsUsed(question: any) {
    const cat = question.category?.id ?? null;
    const diff = question.difficulty ?? null;
    if (!cat || diff == null) return;
    const key = `${cat}::${diff}`;
    this._usedCells = new Set(this._usedCells).add(key);
  }

  private _isCellUsed(catId: string, diffId: number | string) {
    return (this.usedCells ?? this._usedCells).has(`${catId}::${diffId}`);
  }

  private _onCellClick(categoryId: string, difficulty: number): void {
    this.dispatchEvent(new CustomEvent(CustomEventType.JEOPARDY_CELL_CLICKED, {
      detail: { categoryId, difficulty },
      bubbles: true,
      composed: true,
    }));
  }

  render() {
    const quiz = this.quiz;
    if (!quiz) {
      return html`<div style="padding:24px;">Kein Quiz geladen</div>`;
    }

    const categories = quiz.categories ?? [];
    const difficulties = [...(quiz.difficulties ?? [])].sort((a, b) => a - b);

    const palette = [
      'var(--color-pink)',
      'var(--color-lemon)',
      'var(--color-mint)',
      'var(--color-sand)',
      'var(--color-lime)',
    ];

    return html`
      <div
        class="grid-wrap"
        style="grid-template-columns: repeat(${categories.length}, 1fr); grid-template-rows: 0.5fr repeat(${difficulties.length}, 1fr);"
      >
        ${categories.map((c: any) => html`<div class="category-cell"><span class="category-label">${c.name}</span></div>`)}
        ${difficulties.map((d: number) => categories.map((c: any, idx: number) => {
      const isUsed = this._isCellUsed(c.id, d);
      const bg = palette[idx % palette.length];

      return html`
            <button
              class="cell-btn"
              style="background-color: ${bg} !important"
              ?disabled=${isUsed}
              @click=${() => this._onCellClick(c.id, d)}
            >
              <span class="cell-value">${d}</span>
            </button>
          `;
    }))}
      </div>

      ${this._showPopup && this.popupQuestion ? html`
        <div class="overlay">
          <div class="popup">
            <div class="popup-meta">${this.popupQuestion.category?.name ?? '-'} // ${this.popupQuestion.difficulty ?? '-'}</div>
            <div class="popup-question">${this.popupQuestion.question}</div>
            ${this.showAnswer ? html`<div class="popup-answer">Antwort: ${this.popupQuestion.answer}</div>` : ''}
          </div>
        </div>
      ` : ''}
    `;
  }
}
